import React, { useState } from 'react';
import { uploadReviewImage, submitReview } from '../lib/api';
import './ReviewModal.css';

function StarIcon({ filled, size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? '#F59E0B' : 'none'} stroke={filled ? '#F59E0B' : '#C9CBCF'} strokeWidth="1.5">
      <path d="M12 2.5l3.09 6.26 6.91 1-5 4.87 1.18 6.88L12 17.77l-6.18 3.74L7 14.63l-5-4.87 6.91-1L12 2.5z" />
    </svg>
  );
}

export default function ReviewModal({ open, item, orderId, onClose, onSubmitted }) {
  const [rating, setRating]   = useState(0);
  const [hover, setHover]     = useState(0);
  const [comment, setComment] = useState('');
  const [images, setImages]   = useState([]); // [{url, public_id}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');

  if (!open || !item) return null;

  const reset = () => {
    setRating(0); setHover(0); setComment('');
    setImages([]); setError(''); setSubmitting(false); setUploading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 5) {
      setError('You can attach up to 5 photos.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      for (const file of files) {
        const result = await uploadReviewImage(file);
        setImages(prev => [...prev, result]);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setError('');
    if (rating < 1) { setError('Please select a star rating.'); return; }
    if (comment.trim().length < 5) { setError('Please write a few words about the product.'); return; }

    setSubmitting(true);
    try {
      const review = await submitReview({
        orderId,
        orderItemIndex: item.orderItemIndex,
        rating,
        comment,
        images,
      });
      onSubmitted(review);
      reset();
    } catch (err) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rvm-overlay" onClick={handleClose}>
      <div className="rvm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="rvm-close" onClick={handleClose} aria-label="Close">✕</button>

        <div className="rvm-product">
          <img src={item.image} alt={item.name} className="rvm-product__img" />
          <div>
            <p className="rvm-product__name">{item.name}</p>
            {(item.selectedSize || item.selectedPlanter || item.selectedColor) && (
              <p className="rvm-product__variant">
                {[item.selectedSize, item.selectedPlanter, item.selectedColor].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>

        <div className="rvm-section">
          <label className="rvm-label">Your rating</label>
          <div className="rvm-stars" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                type="button"
                key={n}
                className="rvm-star-btn"
                onMouseEnter={() => setHover(n)}
                onClick={() => setRating(n)}
                aria-label={`${n} star`}
              >
                <StarIcon filled={n <= (hover || rating)} />
              </button>
            ))}
          </div>
        </div>

        <div className="rvm-section">
          <label className="rvm-label" htmlFor="rvm-comment">Tell us about the plant you received</label>
          <textarea
            id="rvm-comment"
            className="rvm-textarea"
            rows={4}
            maxLength={2000}
            placeholder="How was the packaging? Was the plant healthy on arrival? Would you recommend it?"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div className="rvm-section">
          <label className="rvm-label">Add photos of the received product (optional)</label>
          <div className="rvm-photos">
            {images.map((img, i) => (
              <div className="rvm-photo" key={img.public_id || i}>
                <img src={img.url} alt={`Upload ${i + 1}`} />
                <button type="button" className="rvm-photo__remove" onClick={() => removeImage(i)} aria-label="Remove photo">✕</button>
              </div>
            ))}
            {images.length < 5 && (
              <label className="rvm-photo rvm-photo--add">
                {uploading ? <span className="rvm-spinner" /> : <span>+</span>}
                <input type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={handleFiles} disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        {error && <p className="rvm-error">{error}</p>}

        <button className="rvm-submit" onClick={handleSubmit} disabled={submitting || uploading}>
          {submitting ? 'Submitting…' : 'Submit Review'}
        </button>
      </div>
    </div>
  );
}